import { analyticsLastDays, shiftAnalyticsDate } from './analyticsDates';
import { summarizeAnalyticsRows } from './analyticsSummary';
import { exactPeriodAudience } from './analyticsAudience';

export const ATTRIBUTION_WINDOWS = ['account_default', '1d_click', '7d_click', '28d_click', '1d_view', '7d_view', '7d_click_1d_view'] as const;
export type ReportingAttribution = typeof ATTRIBUTION_WINDOWS[number];
const suffixes: Record<string, string> = { '1d_click': '1dClick', '7d_click': '7dClick', '28d_click': '28dClick', '1d_view': '1dView', '7d_view': '7dView', '7d_click_1d_view': '7dClick1dView' };
export function reportingAttribution(value: unknown): ReportingAttribution {
  if (value == null || value === '') return 'account_default';
  if (!ATTRIBUTION_WINDOWS.includes(value as ReportingAttribution)) throw new Error('Unsupported attribution window');
  return value as ReportingAttribution;
}
export function reportingDates(start: unknown, end: unknown, now = new Date()) {
  const fallback = analyticsLastDays(30, 'UTC', now);
  const startDate = start == null ? fallback.startDate : String(start);
  const endDate = end == null ? fallback.endDate : String(end);
  shiftAnalyticsDate(startDate, 0); shiftAnalyticsDate(endDate, 0);
  const days = (Date.parse(endDate) - Date.parse(startDate)) / 86400000 + 1;
  if (days < 1 || days > 732) throw new Error('Choose a reporting period of 1–732 days');
  return {startDate, endDate};
}
export function reportedNumber(value: unknown): number | null {
  if (value == null || value === '' || typeof value === 'boolean') return null;
  const n = Number(value); return Number.isFinite(n) && n >= 0 ? n : null;
}
export function projectReportingRow(row: Record<string, any>, attribution: ReportingAttribution): Record<string, any> {
  const extra = row.extraMetrics && typeof row.extraMetrics === 'object' ? row.extraMetrics : {};
  const suffix = suffixes[attribution];
  // Never add overlapping click/view windows to manufacture a combined report.
  const selected = (key: string) => reportedNumber(suffix ? extra[key + suffix] : row[key]);
  const purchases = selected('purchases'), purchaseValue = selected('purchaseValue'), leads = selected('leads');
  return {...row, purchases, purchaseValue, leads, addToCart:selected('addToCart'), initiateCheckout:selected('initiateCheckout'), conversions: purchases, conversionValue: purchaseValue,
    spend: reportedNumber(row.spend), impressions: reportedNumber(row.impressions), clicks: reportedNumber(row.clicks),
    reach: null, frequency: null, attribution};
}
export function reportingSummary(rows: Array<Record<string, any>>): Record<string, number | null> {
  const summary = summarizeAnalyticsRows(rows);
  return {...summary, cpa: summary.cpp};
}
/** Daily delivery/outcomes plus an exact, non-additive audience only for one fully reported ad. */
export function reportingSummaryForWindow(rows:Array<Record<string,any>>,startDate:string,endDate:string):Record<string,number|null>{
  const dates=reportingDates(startDate,endDate);
  const selected=rows.filter(row=>row.date>=dates.startDate&&row.date<=dates.endDate&&row.reported!==false);
  return {...reportingSummary(selected),...exactPeriodAudience(selected,startDate,endDate)};
}
export function reportingByDate(rows: Array<Record<string, any>>, startDate: string, endDate: string): Array<Record<string, any> & {date:string}> {
  const groups = new Map<string, Array<Record<string, any>>>();
  for (const row of rows) groups.set(row.date, [...(groups.get(row.date) || []), row]);
  const result = [];
  for (let date = startDate; date <= endDate; date = shiftAnalyticsDate(date, 1)) {
    const daily = groups.get(date) || [];
    // A missing date is unavailable, not a measured zero-delivery date.
    const summary = daily.length ? reportingSummary(daily) : Object.fromEntries(Object.keys(reportingSummary([])).map(k => [k, null]));
    result.push({date, ...summary, initiateCheckout: summary.checkoutInitiated, engagements: summary.postEngagement,
      likes: summary.postReactions, shares: summary.postShares, comments: summary.postComments, reported: daily.length > 0});
  }
  return result;
}
/** Totals cover supplied reporting days; absent days remain chart gaps. */
export function reportingPeriod(rows: Array<Record<string, any>>, startDate: string, endDate: string) {
  const dates = reportingDates(startDate, endDate);
  const selected = rows.filter(row => row.date >= dates.startDate && row.date <= dates.endDate);
  const reported = selected.filter(row => row.reported !== false);
  const totalDays = (Date.parse(endDate) - Date.parse(startDate)) / 86400000 + 1;
  const reportedDays = new Set(reported.map(row => row.date)).size;
  return {summary: reported.length ? reportingSummaryForWindow(reported,startDate,endDate) : null,
    totalDays, reportedDays, complete: reportedDays === totalDays};
}
export function purchasesByWeekday(rows: Array<{date:string;purchases?:number|null;reported?:boolean}>) {
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day,index)=>{
    const selected=rows.filter(row=>new Date(row.date+'T00:00:00Z').getUTCDay()===index);
    const reported = selected.filter(row => row.reported !== false);
    return {day, reportedDays: reported.length, totalDays: selected.length,
      purchases: !reported.length || reported.some(row => row.purchases == null) ? null : reported.reduce((sum,row) => sum + row.purchases!, 0)};
  });
}
function csvCell(value: unknown) {
  let text = value == null ? '' : String(value);
  // Spreadsheet formula injection can arrive through provider-supplied names.
  if (/^[\s]*[=+\-@]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}
export function analyticsCsv(rows: Array<Record<string, any>>, accounts: Array<Record<string, any>>, ads: Array<Record<string, any>>, attribution: ReportingAttribution, generatedAt = new Date().toISOString()) {
  const accountMap = new Map(accounts.map(a => [a.id, a]));
  const adMap = new Map(ads.map(a => [`${a.adAccountId}:${a.externalAdId}`, a]));
  const headers = ['source','account','account_id','currency','timezone','date','ad','ad_id','attribution','spend','impressions','clicks','purchases','attributed_revenue','leads','exported_at'];
  const values = rows.map(raw => {
    const row = projectReportingRow(raw, attribution), account = accountMap.get(row.adAccountId);
    if (!account) throw new Error('Export row does not belong to the selected accounts');
    const ad = adMap.get(`${row.adAccountId}:${row.entityId}`);
    return [account.isDemo ? 'Simulated test data' : 'Stored provider report',account.accountName,account.externalAccountId,account.currency,account.timezone,row.date,ad?.name || '',row.entityId,attribution,row.spend,row.impressions,row.clicks,row.purchases,row.purchaseValue,row.leads,generatedAt];
  });
  return '\uFEFF' + [headers, ...values].map(row => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
